import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import NumberFormat from "react-number-format";
import { Input as BsInput, Label as BsLabel, FormGroup } from "reactstrap";
import Email from "../../../assets/Email";
import Password from "../../../assets/Password";
import Valid from "../../../assets/Valid";
import Error from "../../../assets/Error";
import "react-responsive-select/dist/ReactResponsiveSelect.css";
import RRS from "react-responsive-select";
import "react-dates/initialize";
import { SingleDatePicker, DateRangePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import INPUTS_CONST from "../../../constants/inputs";
import moment from "moment";
import "./Input.sass";
import { ArrowDown } from "../../../assets/Arrows";
import { FieldArray } from "formik";
import GLOBAL_CONST from "../../../constants/global";

const ErrorHelper = ({ children, ...props }) => (
  <span className="error-helper" aria-live="polite" {...props}>
    Error: {children}
  </span>
);

const Input = ({
  labelProps: { children: labelChildren, ...restOfLabelProps },
  children,
  className,
  valid,
  hasIcon,
  error,
  field,
  form: { touched, errors },
  ...props
}) => {
  const InputIconMap = {
    email: Email,
    password: Password
  };
  const shouldShowError = error || (touched[field.name] && errors[field.name]);
  let Icon = hasIcon && InputIconMap[props.type];
  Icon = valid ? Valid : Icon;
  Icon = shouldShowError ? Error : Icon;
  const needsIconSpace = hasIcon || valid || shouldShowError;
  return (
    <React.Fragment>
      {labelChildren && labelChildren.length && (
        <BsLabel
          htmlFor={props.id}
          {...restOfLabelProps}
          dangerouslySetInnerHTML={{ __html: labelChildren }}
        />
      )}
      <div className={`input-tool-wrapper ${needsIconSpace ? "has-icon" : ""}`}>
        {needsIconSpace && (
          <div className="input-icon" aria-hidden="true">
            {Icon && <Icon />}
          </div>
        )}
        <BsInput
          className={`${className} ${shouldShowError ? "input-error" : ""}`}
          {...field}
          {...props}
        />
      </div>
      {children && <div className="helper-text">{children}</div>}
      {shouldShowError && <ErrorHelper>{errors[field.name]}</ErrorHelper>}
    </React.Fragment>
  );
};

Input.defaultProps = {
  type: "text",
  labelProps: {},
  // form and field props are intended if using formik's
  // field component to wrap the input
  form: {
    touched: {},
    errors: {}
  },
  field: {}
};

Input.propTypes = {
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  valid: PropTypes.bool,
  hasIcon: PropTypes.bool,
  id: PropTypes.string.isRequired
};

const Label = ({ children, ...props }) => {
  return <BsLabel {...props}>{children}</BsLabel>;
};

export const CheckBox = ({
  field,
  // form: { errors, touched, setFieldValue },
  children,
  className,
  label,
  ...props
}) => {
  return (
    <BsLabel
      className={`checkbox-container ${className || ""}`}
      style={props.style}
    >
      <BsInput {...field} {...props} />
      <span className="checkmark" />
      <span className={"sr-only"}>Select or deselect {props.name}</span>
      {label}
    </BsLabel>
  );
};

CheckBox.defaultProps = {
  type: "checkbox"
};

export const CheckBoxGroup = ({
  setFieldValue,
  checkboxes,
  values,
  errors,
  touched,
  label,
  className,
  children,
  name,
  ...props
}) => {
  const shouldShowError = touched[name] && errors[name];
  return (
    <fieldset className="checkbox-group">
      <legend dangerouslySetInnerHTML={{ __html: label }} />
      <FieldArray
        name={name}
        render={arrayHelpers => (
          <React.Fragment>
            {checkboxes.map(checkbox => (
              <FormGroup key={checkbox.value} check>
                <CheckBox
                  name={name}
                  value={checkbox.value}
                  label={checkbox.label}
                  checked={
                    values[name].findIndex(
                      obj => (obj.Id || obj.ItemId) === checkbox.Id
                    ) > -1
                  }
                  onChange={e => {
                    let intendedSelections = [...values[name]];
                    if (e.target.checked) {
                      arrayHelpers.push(checkbox.value);
                      intendedSelections.push(checkbox);
                    } else {
                      const idx = intendedSelections.findIndex(
                        obj => (obj.Id || obj.ItemId) === checkbox.Id
                      );
                      if (idx > -1) {
                        intendedSelections.splice(idx, 1);
                        setFieldValue(name, intendedSelections);
                      }
                    }

                    if (
                      checkbox.mutuallyExclusiveTo &&
                      e.target.checked &&
                      intendedSelections.length
                    ) {
                      intendedSelections = intendedSelections.filter(value => {
                        return !checkbox.mutuallyExclusiveTo.includes(
                          value["value"] || value["ItemName"]
                        );
                      }, []);
                      setFieldValue(name, intendedSelections);
                    }
                  }}
                  {...props}
                />
              </FormGroup>
            ))}
          </React.Fragment>
        )}
      />
      {shouldShowError && <ErrorHelper>{errors[name]}</ErrorHelper>}
    </fieldset>
  );
};

CheckBoxGroup.defaultProps = {};

CheckBoxGroup.propTypes = {
  checkboxes: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired
};

export const RadioButton = ({ children, ...props }) => {
  return (
    <BsLabel className="radio-container">
      <BsInput {...props} />
      <span className="radio-checkmark" />
      {children}
    </BsLabel>
  );
};

RadioButton.defaultProps = {
  type: "radio",
  name: "radio"
};

const RadioGroup = ({
  radios,
  label,
  name,
  inline,
  errors,
  touched,
  values,
  ...props
}) => {
  const shouldShowError = touched[name] && errors[name];
  const generateRadios = () => {
    return radios.map(({ id, label: radioLabel, ...rest }) => (
      <FormGroup key={id} check inline={inline}>
        <RadioButton
          type="radio"
          name={name}
          checked={String(values[name]) === String(rest.value)}
          {...rest}
          onChange={props.onChange}
        >
          {radioLabel}
        </RadioButton>
      </FormGroup>
    ));
  };
  return (
    <FormGroup tag="fieldset" className="radio-group">
      <legend>{label}</legend>
      {generateRadios()}
      {shouldShowError && <ErrorHelper>{errors[name]}</ErrorHelper>}
    </FormGroup>
  );
};
RadioGroup.defaultProps = {
  inline: false,
  touched: {},
  errors: {},
  values: {}
};

RadioGroup.propTypes = {
  radios: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      value: PropTypes.any
    })
  )
};

const Select = ({
  labelProps: { children: labelChildren, ...restOfLabelProps },
  children,
  className,
  valid,
  hasIcon,
  field,
  form: { values, touched, errors, handleChange, handleBlur },
  getSelectedValue,
  ...props
}) => {
  const shouldShowError = touched[field.name] && errors[field.name];
  return (
    <React.Fragment>
      {labelChildren && labelChildren.length && (
        <BsLabel htmlFor={props.id} {...restOfLabelProps}>
          {labelChildren}
        </BsLabel>
      )}
      <div className="rrs__wrapper">
        <div className="rrs__button__presentation">
          <ArrowDown />
        </div>
        <RRS
          className={`${className} ${shouldShowError ? "input-error" : ""}`}
          {...field}
          {...props}
          onChange={newValue => {
            if (values[field.name] !== newValue.value) {
              handleChange({
                target: {
                  value: newValue.value,
                  name: newValue.name
                }
              });
              if (getSelectedValue) {
                getSelectedValue(newValue.value);
              }
            }
          }}
          onBlur={newValue => {
            handleBlur({
              target: {
                value: newValue.value,
                name: newValue.name
              }
            });
          }}
          selectedValue={values[field.name]}
        />
      </div>
      {shouldShowError && <ErrorHelper>{errors[field.name]}</ErrorHelper>}
    </React.Fragment>
  );
};

Select.defaultProps = {
  labelProps: {},
  // form and field props are intended if using formik's
  // field component to wrap the input
  form: {
    touched: {},
    errors: {},
    values: {},
    handleBlur: () => {},
    handleChange: () => {}
  },
  field: {}
};

Input.propTypes = {
  valid: PropTypes.bool,
  id: PropTypes.string.isRequired
};

const DatePickerWithFormik = ({
  name,
  form: { setFieldValue, values, errors, touched },
  field,
  labelProps: { children: labelChildren, ...restOfLabelProps },
  ...props
}) => {
  let dateObj = null;
  const shouldShowError = touched[field.name] && errors[field.name];
  const [focus, setfocus] = useState(null);
  setInitialValue();

  function onDateChange(selectedDateObj) {
    setFieldValue(field.name, setDateFormatValues(selectedDateObj));
  }

  function setInitialValue() {
    dateObj = values[field.name]
      ? moment(values[field.name], GLOBAL_CONST.DISPLAYDATEFORMAT)
      : null;
  }

  function setDateFormatValues(selectedDateObj) {
    return selectedDateObj
      ? moment(selectedDateObj).format(GLOBAL_CONST.DISPLAYDATEFORMAT)
      : null;
  }

  function onFocusChange({ focused }) {
    setfocus(focused);
  }

  return (
    <div className="App">
      {labelChildren && labelChildren.length && (
        <BsLabel
          htmlFor={props.id}
          {...restOfLabelProps}
          dangerouslySetInnerHTML={{ __html: labelChildren }}
        />
      )}
      <SingleDatePicker
        date={dateObj}
        onDateChange={onDateChange}
        focused={focus}
        onFocusChange={onFocusChange}
        id="SDP"
        numberOfMonths={1}
        isOutsideRange={() => false}
        block={true}
        placeholder={labelChildren}
      />
      {shouldShowError && <ErrorHelper>{errors[field.name]}</ErrorHelper>}
    </div>
  );
};

const NumberFormating = ({
  labelProps: { children: labelChildren, ...restOfLabelProps },
  children,
  className,
  valid,
  hasIcon,
  error,
  field,
  form: { touched, errors },
  hasDollarIcon,
  ...props
}) => {
  const InputIconMap = {
    email: Email,
    password: Password
  };
  const shouldShowError = error || (touched[field.name] && errors[field.name]);
  let Icon = hasIcon && InputIconMap[props.type];
  Icon = valid ? Valid : Icon;
  Icon = shouldShowError ? Error : Icon;
  const needsIconSpace = hasIcon || valid || shouldShowError;
  return (
    <div className="App">
      {labelChildren && labelChildren.length && (
        <BsLabel
          htmlFor={props.id}
          {...restOfLabelProps}
          dangerouslySetInnerHTML={{ __html: labelChildren }}
        />
      )}
      <div className={`input-tool-wrapper ${needsIconSpace ? "has-icon" : ""}`}>
        {needsIconSpace && (
          <div className="input-icon" aria-hidden="true">
            {Icon && <Icon />}
          </div>
        )}
        <NumberFormat
          className={`${className} ${
            shouldShowError ? "input-error" : ""
          } ${"form-control"}`}
          {...field}
          {...props}
          thousandSeparator={true}
          prefix={hasDollarIcon ? "$" : ""}
        />
      </div>
      {children && <div className="helper-text">{children}</div>}
      {shouldShowError && <ErrorHelper>{errors[field.name]}</ErrorHelper>}
    </div>
  );
};

NumberFormating.defaultProps = {
  type: "text",
  labelProps: {},
  form: {
    touched: {},
    errors: {}
  },
  field: {}
};

NumberFormating.propTypes = {
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  valid: PropTypes.bool,
  hasIcon: PropTypes.bool,
  id: PropTypes.string.isRequired
};

export {
  Input,
  Label,
  Select,
  RadioGroup,
  ErrorHelper,
  DatePickerWithFormik,
  NumberFormating
};
